import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';

import FormCard from '../../../../components/FormCard';
import LogBodyPanel from './LogBodyPanel';
import LogSituacaoBadge from './LogSituacaoBadge';
import { formatDuracao } from '../../../../utils/logAuditoria';

// ============================================================================
// Painel de detalhe de um log de auditoria selecionado.
// Recebe o log (com bodies decoded + wire + headers + errorData) e renderiza:
//   - card "DETALHE DA REQUISICAO" com metadata (metodo, status, duracao)
//   - LogBodyPanel para request e response (com toggle Decoded/Wire)
//   - LogBodyPanel adicional para errorData (quando presente)
//
// emptyHint: texto exibido quando nenhum log foi selecionado ainda.
// ============================================================================

const LogDetalhe = ({ data, loading, idSelecionado, emptyHint }) => {
  const theme = useTheme();

  if (!idSelecionado) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          color: theme.palette.text.secondary,
          border: '1px dashed',
          borderColor: theme.palette.divider,
          borderRadius: 1,
          backgroundColor: theme.palette.background.default
        }}
      >
        <Typography variant="body2">
          {emptyHint || 'Selecione uma linha acima para inspecionar o conteúdo da requisição e da resposta.'}
        </Typography>
      </Box>
    );
  }

  if (loading || !data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <FormCard title={'DETALHE DA REQUISIÇÃO'} border subtitle={data.descricaoUrl || '—'}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Método</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {data.codigoHttpMethod || '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {data.codigoStatusCode ?? '—'}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Duração</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatDuracao(data.numeroDuracaoMs)}
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="caption" color="text.secondary">Resultado</Typography>
            <Box sx={{ mt: 0.5 }}>
              <LogSituacaoBadge
                flagSuccesso={data.flagSuccesso}
                codigoStatusCode={data.codigoStatusCode}
              />
            </Box>
          </Grid>
        </Grid>
      </FormCard>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <LogBodyPanel
            titulo="Request body"
            bodyDecoded={data.descricaoRequestBodyDecoded}
            bodyWire={data.descricaoRequestBodyWire}
            format={data.descricaoRequestBodyFormat}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <LogBodyPanel
            titulo="Response body"
            bodyDecoded={data.descricaoResponseBodyDecoded}
            bodyWire={data.descricaoResponseBodyWire}
            format={data.descricaoResponseBodyFormat}
          />
        </Grid>
        {data.descricaoErrorData && (
          <Grid item xs={12}>
            <LogBodyPanel
              titulo="Error data"
              bodyDecoded={data.descricaoErrorData}
              bodyWire={null}
              format={null}
            />
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};

export default LogDetalhe;
